import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Users,
  Search,
  Download,
  Mail,
  ChevronDown,
  ChevronUp,
  ChevronLeft,
  ChevronRight,
  Tv,
  Phone,
  Network,
  Calendar,
  X,
  FileSpreadsheet,
  Info,
  TrendingUp,
} from "lucide-react";
import { toast } from "react-toastify";
import Navbar from "../../components/Navbar";
import Loader from "../../components/Loader";
import * as XLSX from "xlsx";
import usePotentialPublishers from "../../hooks/usePotentialPublishers";
import affiliateMarketingService from "../../requests/admin/AffiliateMarketingService";

const PotentialPublishers = () => {
  const [showDateFilter, setShowDateFilter] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportFormat, setExportFormat] = useState("xlsx");
  const [exportingFiltered, setExportingFiltered] = useState(false);
  const [exportingAll, setExportingAll] = useState(false);
  const [emailExportNotice, setEmailExportNotice] = useState(null);
  const [stats, setStats] = useState({
    total: 0,
    mtn: 0,
    vodafone: 0,
    airtelTigo: 0,
    thisMonth: 0,
    growthRate: 0,
  });

  const {
    publishers,
    loading,
    error,
    pagination,
    filters,
    debouncedSearch,
    changePage,
    changeItemsPerPage,
    updateSort,
    updateDateFilter,
    updateNetworkFilter,
    clearDateFilter,
    clearAllFilters,
    fetchAllMatchingForExport,
  } = usePotentialPublishers();

  const calculateStats = (publishersList) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    const thisMonthPublishers = publishersList.filter((p) => {
      const createdDate = new Date(p.created_at);
      return (
        createdDate.getMonth() === currentMonth &&
        createdDate.getFullYear() === currentYear
      );
    });

    const lastMonth = new Date(currentYear, currentMonth - 1);
    const lastMonthPublishers = publishersList.filter((p) => {
      const createdDate = new Date(p.created_at);
      return (
        createdDate.getMonth() === lastMonth.getMonth() &&
        createdDate.getFullYear() === lastMonth.getFullYear()
      );
    });

    const growthRate =
      lastMonthPublishers.length > 0
        ? (
            ((thisMonthPublishers.length - lastMonthPublishers.length) /
              lastMonthPublishers.length) *
            100
          ).toFixed(1)
        : 0;

    setStats({
      total: pagination.totalItems || publishersList.length,
      mtn: publishersList.filter((p) => p.network === "MTN").length,
      vodafone: publishersList.filter((p) => p.network === "VDF").length,
      airtelTigo: publishersList.filter((p) => p.network === "TGO").length,
      thisMonth: thisMonthPublishers.length,
      growthRate: growthRate,
    });
  };

  useEffect(() => {
    calculateStats(publishers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publishers, pagination.totalItems]);

  const getNetworkColor = (network) => {
    switch (network) {
      case "MTN":
        return "bg-yellow-100 text-yellow-700 border-yellow-300";
      case "VDF":
        return "bg-red-100 text-red-700 border-red-300";
      case "TGO":
        return "bg-blue-100 text-blue-700 border-blue-300";
      default:
        return "bg-gray-100 text-gray-700 border-gray-300";
    }
  };

  const getNetworkDisplayName = (network) => {
    switch (network) {
      case "VDF":
        return "Telecel";
      case "TGO":
        return "AirtelTigo";
      default:
        return network;
    }
  };

  const buildExportRows = (list) =>
    list.map((publisher, index) => ({
      "#": index + 1,
      "Publisher ID": publisher.id,
      Name: publisher.name,
      "Phone Number": publisher.msisdn,
      Network: publisher.network,
      "Influencer Code":
        publisher.influencer_code !== "N/A" ? publisher.influencer_code : "-",
      "Registration Date": new Date(publisher.created_at).toLocaleDateString(),
      "Last Updated": new Date(publisher.updated_at).toLocaleDateString(),
    }));

  const handleFilteredExportDownload = async () => {
    if (!hasActiveFilters) {
      toast.info("Apply a filter (date range, search, or network) to enable export.");
      return;
    }

    setExportingFiltered(true);
    try {
      // Pull ALL rows matching the current filters from backend (not just current page)
      const allMatching = await fetchAllMatchingForExport();
      const dataToExport = buildExportRows(allMatching);

      const ws = XLSX.utils.json_to_sheet(dataToExport);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Publishers");

      // Set column widths
      const colWidths = [
        { wch: 5 },
        { wch: 12 },
        { wch: 25 },
        { wch: 15 },
        { wch: 10 },
        { wch: 18 },
        { wch: 18 },
        { wch: 15 },
      ];
      ws["!cols"] = colWidths;

      const tagParts = [];
      if (filters.dateFrom || filters.dateTo) {
        tagParts.push(`${filters.dateFrom || "any"}_to_${filters.dateTo || "any"}`);
      }
      if (filters.network) tagParts.push(getNetworkDisplayName(filters.network));
      if (filters.search) tagParts.push("search");

      const fileName = `potential_publishers_${
        new Date().toISOString().split("T")[0]
      }${tagParts.length ? `_${tagParts.join("_")}` : ""}.xlsx`;
      XLSX.writeFile(wb, fileName);

      toast.success(
        `Exported ${dataToExport.length} publishers successfully!`
      );
      setShowExportModal(false);
    } catch (error) {
      console.error("Export error:", error);
      toast.error(error?.message || "Failed to export data");
    } finally {
      setExportingFiltered(false);
    }
  };

  const handleExportAllToEmail = async () => {
    setExportingAll(true);
    setEmailExportNotice(null);
    try {
      const response =
        await affiliateMarketingService.requestAllPublishersExportToEmail();

      if (response.status) {
        toast.success("Export started. Check your email for the Excel file.");
        setEmailExportNotice(
          response.message ||
            "We’re preparing your full export. Please check your email shortly."
        );
      } else {
        toast.error(response.message || "Failed to start email export");
      }
    } catch (e) {
      toast.error("Failed to start email export");
    } finally {
      setExportingAll(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const statCards = [
    {
      title: "Total Publishers",
      value: stats.total,
      icon: <Users className="w-6 h-6" />,
      color: "red",
      bgGradient: "from-red-50 to-red-100",
      textColor: "text-[#FF6C6C]",
    },
    {
      title: "MTN Network",
      value: stats.mtn,
      icon: <Phone className="w-6 h-6" />,
      color: "yellow",
      bgGradient: "from-yellow-50 to-yellow-100",
      textColor: "text-yellow-600",
    },
    {
      title: "Telecel Network",
      value: stats.vodafone,
      icon: <Network className="w-6 h-6" />,
      color: "red",
      bgGradient: "from-red-50 to-red-100",
      textColor: "text-red-600",
    },
    {
      title: "This Month",
      value: stats.thisMonth,
      icon: <TrendingUp className="w-6 h-6" />,
      color: "green",
      bgGradient: "from-green-50 to-green-100",
      textColor: "text-green-600",
      badge: `${stats.growthRate > 0 ? "+" : ""}${stats.growthRate}%`,
    },
  ];

  const requestSort = (key) => {
    let direction = "asc";
    if (filters.sortBy === key && filters.sortOrder === "asc") {
      direction = "desc";
    }
    updateSort(key, direction);
  };

  const getSortIcon = (columnKey) => {
    return filters.sortBy === columnKey ? (
      filters.sortOrder === "asc" ? (
        <ChevronUp size={16} />
      ) : (
        <ChevronDown size={16} />
      )
    ) : (
      <div className="flex flex-col ml-2">
        <ChevronUp size={12} />
        <ChevronDown size={12} />
      </div>
    );
  };

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value);
  };

  const handleItemsPerPageChange = (e) => {
    const newLimit = parseInt(e.target.value);
    changeItemsPerPage(newLimit);
  };

  // Handle clicking outside date filter dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showDateFilter && !event.target.closest(".date-filter-container")) {
        setShowDateFilter(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDateFilter]);

  const hasActiveFilters =
    filters.search || filters.dateFrom || filters.dateTo || filters.network;

  const getDisplayTotal = () => {
    return pagination?.totalItems || 0;
  };

  // Generate pagination numbers efficiently
  const getPaginationNumbers = () => {
    const totalPages = pagination?.totalPages || 1;
    const currentPage = pagination?.currentPage || 1;
    const numbers = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) numbers.push(i);
    } else {
      if (currentPage <= 4) {
        for (let i = 1; i <= 5; i++) numbers.push(i);
        numbers.push("...");
        numbers.push(totalPages);
      } else if (currentPage >= totalPages - 3) {
        numbers.push(1);
        numbers.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) numbers.push(i);
      } else {
        numbers.push(1);
        numbers.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) numbers.push(i);
        numbers.push("...");
        numbers.push(totalPages);
      }
    }
    return numbers;
  };

  if (loading && publishers.length === 0) {
    return <Loader />;
  }

  return (
    <>
      <Navbar page={"Potential Publishers"} />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-red-50/30">
        <div className="p-6">
          {/* Header Section */}
          <div className="mb-8">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col gap-4 justify-between items-start mb-6 md:flex-row md:items-center"
            >
              <div>
                <h1 className="flex gap-3 items-center text-3xl font-bold text-gray-900">
                  <div className="p-3 bg-gradient-to-br from-[#FF6C6C] to-[#FF5252] rounded-xl shadow-lg">
                    <Tv className="w-8 h-8 text-white" />
                  </div>
                  Potential Publishers
                </h1>
                <p className="mt-2 text-gray-600">
                  Manage and track all potential affiliate publishers
                </p>
              </div>
              <div className="flex flex-col gap-2 w-full sm:flex-row sm:w-auto">
                {/* <button
                  onClick={() => refetch()}
                  className="flex gap-2 justify-center items-center px-4 py-2.5 w-full text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 shadow-sm transition-all hover:bg-gray-50 sm:w-auto"
                >
                  <RefreshCw className="w-4 h-4" />
                  Refresh
                </button> */}
                <button
                  onClick={() => setShowExportModal(true)}
                  disabled={!hasActiveFilters}
                  className={`flex gap-2 items-center justify-center px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-300 w-full sm:w-auto ${
                    !hasActiveFilters
                      ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                      : "text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF5252] hover:shadow-lg"
                  }`}
                  title={
                    hasActiveFilters
                      ? "Export filtered data"
                      : "Apply a filter to enable export"
                  }
                >
                  <Download className="w-4 h-4" />
                  <span className="hidden sm:inline">Export (Filtered)</span>
                  <span className="sm:hidden">Export Filtered</span>
                </button>
                <button
                  onClick={handleExportAllToEmail}
                  disabled={exportingAll}
                  className={`flex gap-2 items-center justify-center px-4 py-2.5 text-sm font-medium text-white rounded-lg transition-all duration-300 w-full sm:w-auto ${
                    exportingAll
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-gradient-to-r from-gray-800 to-gray-700 hover:shadow-lg"
                  }`}
                  title="Export all data to your email"
                >
                  <Mail className="w-4 h-4" />
                  {exportingAll ? (
                    <span className="hidden sm:inline">Sending...</span>
                  ) : (
                    <>
                      <span className="hidden sm:inline">Export All (Email)</span>
                      <span className="sm:hidden">Export All</span>
                    </>
                  )}
                </button>
              </div>
            </motion.div>

            {emailExportNotice && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="p-4 mb-6 bg-emerald-50 rounded-xl border border-emerald-200"
              >
                <p className="text-sm font-medium text-emerald-800">
                  Export queued successfully
                </p>
                <p className="mt-1 text-sm text-emerald-700">
                  {emailExportNotice}
                </p>
              </motion.div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
              {statCards.map((card, index) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="overflow-hidden relative bg-white rounded-xl border border-gray-100 shadow-md transition-all duration-300 hover:shadow-lg group"
                >
                  <div
                    className={`absolute inset-0 bg-gradient-to-br ${card.bgGradient} opacity-50`}
                  ></div>
                  <div className="relative p-6">
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <p className="mb-1 text-sm font-medium text-gray-600">
                          {card.title}
                        </p>
                        <div className="flex gap-2 items-baseline">
                          <p className={`text-3xl font-bold ${card.textColor}`}>
                            {card.value}
                          </p>
                          {card.badge && (
                            <span
                              className={`text-xs font-semibold px-2 py-1 rounded-full ${
                                parseFloat(card.badge) >= 0
                                  ? "bg-green-100 text-green-700"
                                  : "bg-red-100 text-red-700"
                              }`}
                            >
                              {card.badge}
                            </span>
                          )}
                        </div>
                      </div>
                      <div
                        className={`p-3 ${card.textColor} bg-white rounded-lg shadow-sm group-hover:scale-110 transition-transform`}
                      >
                        {card.icon}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Guidelines Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-5 mb-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-xl border border-red-200 shadow-sm"
          >
            <div className="flex gap-3 items-start">
              <div className="p-2 bg-[#FF6C6C] rounded-lg">
                <Info className="w-5 h-5 text-white" />
              </div>
              <div className="flex-1">
                <h3 className="mb-2 font-semibold text-red-900">
                  Publisher Management Guidelines
                </h3>
                <ul className="space-y-1 text-sm text-red-800">
                  <li>
                    • Publishers are potential affiliates who have expressed
                    interest in promoting deals
                  </li>
                  <li>
                    • Use the search and filters to find specific publishers by
                    name, phone, or network
                  </li>
                  <li>
                    • Export data for offline analysis or reporting purposes
                  </li>
                  <li>
                    • Monitor growth trends to understand publisher acquisition
                    rates
                  </li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Filters Section (match Subscribers UI) */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-5 mb-6 bg-white rounded-xl border border-gray-100 shadow-md"
          >
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              {/* Search and Filters Container */}
              <div className="flex flex-col flex-1 gap-3 items-start sm:flex-row sm:items-center">
                {/* Search Input */}
                <div className="relative w-full sm:w-72">
                  <input
                    type="text"
                    placeholder="Search publishers..."
                    defaultValue={filters.search}
                    onChange={handleSearchChange}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 outline-none shadow-sm"
                  />
                  <Search
                    size={18}
                    className="absolute left-3 top-1/2 text-gray-400 transform -translate-y-1/2"
                  />
                </div>

                {/* Date Filter Button */}
                <div className="relative w-full date-filter-container sm:w-auto">
                  <button
                    onClick={() => setShowDateFilter(!showDateFilter)}
                    className={`flex items-center justify-center gap-2 px-4 py-3 border rounded-xl transition-all duration-200 w-full sm:w-auto ${
                      filters.dateFrom || filters.dateTo
                        ? "border-[#FF6C6C] bg-[#FF6C6C]/5 text-[#FF6C6C]"
                        : "border-gray-300 hover:border-[#FF6C6C] hover:bg-gray-50"
                    }`}
                  >
                    <Calendar size={18} />
                    <span className="text-sm font-medium">
                      {filters.dateFrom || filters.dateTo
                        ? "Date Filtered"
                        : "Filter by Date"}
                    </span>
                    {(filters.dateFrom || filters.dateTo) && (
                      <X
                        size={16}
                        className="ml-1 hover:bg-[#FF6C6C]/10 rounded-full p-0.5"
                        onClick={(e) => {
                          e.stopPropagation();
                          clearDateFilter();
                        }}
                      />
                    )}
                  </button>

                  {/* Date Filter Dropdown */}
                  {showDateFilter && (
                    <div className="absolute right-0 left-0 top-full z-50 p-4 mt-2 w-full bg-white rounded-xl border border-gray-200 shadow-lg sm:right-auto sm:w-80">
                      <div className="flex justify-between items-center mb-4">
                        <h3 className="text-sm font-semibold text-gray-900">
                          Filter by Date Range
                        </h3>
                        <button
                          onClick={() => setShowDateFilter(false)}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <X size={16} />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            From Date
                          </label>
                          <input
                            type="date"
                            id="dateFrom"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none"
                            max={filters.dateTo || undefined}
                            defaultValue={filters.dateFrom || ""}
                          />
                        </div>

                        <div>
                          <label className="block mb-2 text-sm font-medium text-gray-700">
                            To Date
                          </label>
                          <input
                            type="date"
                            id="dateTo"
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none"
                            min={filters.dateFrom || undefined}
                            defaultValue={filters.dateTo || ""}
                          />
                        </div>
                      </div>

                      <div className="flex justify-between items-center pt-4 mt-6 border-t border-gray-200">
                        <button
                          onClick={() => {
                            clearDateFilter();
                            setShowDateFilter(false);
                          }}
                          className="text-sm text-gray-600 hover:text-gray-800"
                        >
                          Clear
                        </button>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowDateFilter(false)}
                            className="px-4 py-2 text-sm text-gray-600 rounded-lg border border-gray-300 hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={() => {
                              const dateFrom =
                                document.getElementById("dateFrom").value;
                              const dateTo =
                                document.getElementById("dateTo").value;
                              updateDateFilter(dateFrom, dateTo);
                              setShowDateFilter(false);
                            }}
                            className="px-4 py-2 text-sm text-white bg-[#FF6C6C] rounded-lg hover:bg-[#FF6C6C]/90"
                          >
                            Apply Filter
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Network Filter (match channel filter styling) */}
                <div className="relative w-full sm:w-auto">
                  <div className="relative">
                    <select
                      value={filters.network || ""}
                      onChange={(e) => updateNetworkFilter(e.target.value)}
                      className={`w-full sm:w-auto px-4 py-3 pr-10 border rounded-xl focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] transition-all duration-200 outline-none shadow-sm appearance-none ${
                        filters.network
                          ? "border-[#FF6C6C] bg-[#FF6C6C]/5 text-[#FF6C6C]"
                          : "border-gray-300 hover:border-[#FF6C6C] hover:bg-gray-50"
                      }`}
                    >
                      <option value="">All Networks</option>
                      <option value="MTN">MTN</option>
                      <option value="VDF">Telecel</option>
                      <option value="TGO">AirtelTigo</option>
                    </select>
                    <div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                    {filters.network && (
                      <button
                        onClick={() => updateNetworkFilter("")}
                        className="absolute inset-y-0 right-6 flex items-center pr-2 hover:bg-[#FF6C6C]/10 rounded-r-lg"
                      >
                        <X
                          size={14}
                          className="text-[#FF6C6C] hover:text-[#FF6C6C]/80"
                        />
                      </button>
                    )}
                  </div>
                  {filters.network && (
                    <div className="absolute left-0 top-full z-10 p-2 mt-1 w-full bg-white rounded-lg border border-gray-200 shadow-lg sm:w-auto">
                      <div className="flex gap-2 items-center px-2 py-1">
                        <div className="w-2 h-2 rounded-full bg-[#FF6C6C]"></div>
                        <span className="text-xs font-medium text-gray-700">
                          Filtered by: {getNetworkDisplayName(filters.network)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Items Per Page Selector */}
              <div className="flex gap-3 items-center w-full sm:w-auto">
                <label className="text-sm text-gray-600 whitespace-nowrap">
                  Show:
                </label>
                <select
                  value={pagination?.itemsPerPage || 15}
                  onChange={handleItemsPerPageChange}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#FF6C6C]/20 focus:border-[#FF6C6C] outline-none"
                >
                  <option value={15}>15 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
              </div>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="flex items-center gap-2 px-4 py-3 text-sm font-medium text-[#FF6C6C] border border-[#FF6C6C]/30 rounded-xl hover:bg-[#FF6C6C]/5 transition-colors w-full sm:w-auto justify-center"
                >
                  <X size={16} />
                  Clear Filters
                </button>
              )}
            </div>
          </motion.div>

          {/* Table Section */}

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="overflow-hidden bg-white rounded-xl border border-gray-100 shadow-md"
          >
            {/* Table Toolbar (keeps export actions close to results) */}
            <div className="px-6 py-4 bg-white border-b border-gray-100">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex flex-col gap-1">
                  <p className="text-sm text-gray-700">
                    <span className="font-semibold text-[#FF6C6C]">
                      {getDisplayTotal().toLocaleString()}
                    </span>{" "}
                    results
                    {hasActiveFilters && (
                      <span className="ml-2 text-xs font-semibold px-2 py-0.5 rounded-full bg-[#FF6C6C]/10 text-[#FF6C6C] border border-[#FF6C6C]/20">
                        Filtered
                      </span>
                    )}
                  </p>
                  {hasActiveFilters && (
                    <div className="flex flex-wrap gap-2 mt-1">
                      {(filters.dateFrom || filters.dateTo) && (
                        <span className="px-2 py-1 text-xs text-gray-700 bg-gray-50 rounded-lg border border-gray-200">
                          Date: {filters.dateFrom || "Any"} →{" "}
                          {filters.dateTo || "Any"}
                        </span>
                      )}
                      {filters.network && (
                        <span className="px-2 py-1 text-xs text-gray-700 bg-gray-50 rounded-lg border border-gray-200">
                          Network: {getNetworkDisplayName(filters.network)}
                        </span>
                      )}
                      {filters.search && (
                        <span className="px-2 py-1 text-xs text-gray-700 bg-gray-50 rounded-lg border border-gray-200">
                          Search: “{filters.search}”
                        </span>
                      )}
                      <button
                        onClick={clearAllFilters}
                        className="text-xs font-semibold px-2 py-1 rounded-lg border border-[#FF6C6C]/30 text-[#FF6C6C] hover:bg-[#FF6C6C]/5"
                      >
                        Clear all
                      </button>
                    </div>
                  )}
                </div>

                <div className="flex flex-col gap-2 w-full sm:flex-row sm:w-auto sm:items-center sm:justify-end">
                  <button
                    onClick={() => setShowExportModal(true)}
                    disabled={!hasActiveFilters}
                    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg transition-all w-full sm:w-auto ${
                      !hasActiveFilters
                        ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                        : "text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF5252] hover:shadow-lg"
                    }`}
                    title={
                      hasActiveFilters
                        ? "Export filtered data"
                        : "Apply a filter to enable export"
                    }
                  >
                    <Download className="w-4 h-4" />
                    <span className="hidden sm:inline">Export (Filtered)</span>
                    <span className="sm:hidden">Export Filtered</span>
                  </button>

                  <button
                    onClick={handleExportAllToEmail}
                    disabled={exportingAll}
                    className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold text-white rounded-lg transition-all w-full sm:w-auto ${
                      exportingAll
                        ? "bg-gray-400 cursor-not-allowed"
                        : "bg-gradient-to-r from-gray-800 to-gray-700 hover:shadow-lg"
                    }`}
                    title="Export all data to your email"
                  >
                    <Mail className="w-4 h-4" />
                    {exportingAll ? (
                      <span className="hidden sm:inline">Sending...</span>
                    ) : (
                      <>
                        <span className="hidden sm:inline">Export All (Email)</span>
                        <span className="sm:hidden">Export All</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase">
                      #
                    </th>
                    <th
                      onClick={() => requestSort("name")}
                      className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Publisher Info
                        {getSortIcon("name")}
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("msisdn")}
                      className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Contact
                        {getSortIcon("msisdn")}
                      </div>
                    </th>
                    <th
                      onClick={() => requestSort("network")}
                      className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Network
                        {getSortIcon("network")}
                      </div>
                    </th>
                    <th className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase whitespace-nowrap">
                      Referred By
                    </th>
                    <th
                      onClick={() => requestSort("created_at")}
                      className="px-6 py-4 text-xs font-semibold tracking-wider text-left text-gray-600 uppercase whitespace-nowrap cursor-pointer hover:bg-gray-100"
                    >
                      <div className="flex items-center">
                        Registered
                        {getSortIcon("created_at")}
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {error && (
                    <tr>
                      <td colSpan="6" className="px-6 py-6">
                        <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-sm text-red-700">{error}</p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {!error && publishers.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center">
                        <div className="flex flex-col justify-center items-center">
                          <Tv className="mb-3 w-16 h-16 text-gray-300" />
                          <p className="font-medium text-gray-500">
                            No publishers found
                          </p>
                          <p className="mt-1 text-sm text-gray-400">
                            Try adjusting your search or filters
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    publishers.map((publisher, index) => (
                      <motion.tr
                        key={publisher.id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: index * 0.02 }}
                        className="transition-colors duration-150 hover:bg-red-50/30"
                      >
                        <td className="px-6 py-4">
                          <span className="text-sm font-medium text-gray-600">
                            {(pagination.currentPage - 1) *
                              pagination.itemsPerPage +
                              index +
                              1}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-3 items-center">
                            <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-[#FF6C6C] to-[#FF5252] rounded-full shadow-md">
                              <span className="text-sm font-semibold text-white">
                                {publisher.name?.charAt(0)?.toUpperCase() ||
                                  "P"}
                              </span>
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">
                                {publisher.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                ID: {publisher.id}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-2 items-center">
                            <Phone className="w-4 h-4 text-gray-400" />
                            <span className="font-mono text-sm text-gray-900">
                              {publisher.msisdn}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${getNetworkColor(
                              publisher.network
                            )}`}
                          >
                            {getNetworkDisplayName(publisher.network)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {publisher.influencer_code !== "N/A" ? (
                            <div className="inline-flex items-center px-3 py-1 text-xs font-semibold text-[#FF6C6C] bg-gradient-to-r from-red-50 to-orange-50 rounded-lg border border-red-200">
                              <svg
                                className="mr-1.5 w-3 h-3"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                              </svg>
                              <span className="font-mono">
                                {publisher.influencer_code}
                              </span>
                            </div>
                          ) : (
                            <span className="text-sm italic text-gray-400">
                              No referral
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-2 items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {formatDate(publisher.created_at)}
                          </div>
                        </td>
                      </motion.tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {(pagination?.totalPages || 1) > 1 && (
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                <div className="flex flex-col gap-4 justify-between items-center sm:flex-row">
                  <p className="text-sm text-gray-600">
                    Showing{" "}
                    {((pagination?.currentPage || 1) - 1) *
                      (pagination?.itemsPerPage || 15) +
                      1}{" "}
                    to{" "}
                    {Math.min(
                      (pagination?.currentPage || 1) *
                        (pagination?.itemsPerPage || 15),
                      getDisplayTotal()
                    )}{" "}
                    of {getDisplayTotal().toLocaleString()} entries
                  </p>
                  <div className="flex justify-center items-center space-x-1 sm:justify-end sm:space-x-2">
                    <button
                      onClick={() => changePage((pagination?.currentPage || 1) - 1)}
                      disabled={!(pagination?.hasPrev || false)}
                      className={`p-2 rounded ${
                        !(pagination?.hasPrev || false)
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <ChevronLeft size={16} />
                    </button>

                    {/* Smart Pagination Numbers */}
                    <div className="hidden items-center space-x-1 sm:flex">
                      {getPaginationNumbers().map((number, index) => (
                        <button
                          key={index}
                          onClick={() => {
                            if (number !== "...") changePage(number);
                          }}
                          disabled={number === "..."}
                          className={`px-2 sm:px-3 py-1 rounded text-sm ${
                            number === "..."
                              ? "text-gray-400 cursor-default"
                              : (pagination?.currentPage || 1) === number
                              ? "bg-[#FF6C6C] text-white"
                              : "text-gray-600 hover:bg-gray-200"
                          }`}
                        >
                          {number}
                        </button>
                      ))}
                    </div>

                    {/* Mobile pagination - show current page and total */}
                    <div className="flex items-center space-x-2 sm:hidden">
                      <span className="text-sm text-gray-600">
                        Page {pagination?.currentPage || 1} of{" "}
                        {pagination?.totalPages || 1}
                      </span>
                    </div>

                    <button
                      onClick={() => changePage((pagination?.currentPage || 1) + 1)}
                      disabled={!(pagination?.hasNext || false)}
                      className={`p-2 rounded ${
                        !(pagination?.hasNext || false)
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-600 hover:bg-gray-200"
                      }`}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>

        {/* Export Modal */}
        <AnimatePresence>
          {showExportModal && (
            <div className="flex fixed inset-0 z-50 justify-center items-center p-4">
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 backdrop-blur-sm bg-black/50"
                onClick={() => setShowExportModal(false)}
              />
              <motion.div
                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95, y: 20 }}
                className="overflow-hidden relative w-full max-w-md bg-white rounded-2xl shadow-2xl"
              >
                <div className="px-6 py-4 bg-gradient-to-r from-[#FF6C6C] to-[#FF5252]">
                  <div className="flex justify-between items-center">
                    <div className="flex gap-3 items-center">
                      <div className="p-2 rounded-lg bg-white/20">
                        <FileSpreadsheet className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-white">
                          Export Publishers
                        </h3>
                        <p className="text-sm text-red-100">
                          Download {publishers.length} records
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => setShowExportModal(false)}
                      className="transition-colors text-white/80 hover:text-white"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="p-6">
                  <div className="mb-6">
                    <label className="block mb-3 text-sm font-semibold text-gray-700">
                      Export Format
                    </label>
                    <div className="space-y-2">
                      <label className="flex items-center p-3 bg-red-50 rounded-lg border-2 border-[#FF6C6C] cursor-pointer">
                        <input
                          type="radio"
                          name="format"
                          value="xlsx"
                          checked={exportFormat === "xlsx"}
                          onChange={(e) => setExportFormat(e.target.value)}
                          className="w-4 h-4 text-[#FF6C6C]"
                        />
                        <div className="ml-3">
                          <p className="font-semibold text-gray-900">
                            Excel (.xlsx)
                          </p>
                          <p className="text-xs text-gray-600">
                            Best for spreadsheets and data analysis
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div
                    className={`p-4 mb-6 rounded-lg border ${
                      hasActiveFilters
                        ? "bg-red-50 border-red-200"
                        : "bg-gray-50 border-gray-200"
                    }`}
                  >
                    <p
                      className={`text-sm ${
                        hasActiveFilters ? "text-red-900" : "text-gray-900"
                      }`}
                    >
                      <strong>Export Summary:</strong>
                    </p>
                    {hasActiveFilters ? (
                      <ul className="mt-2 space-y-1 text-sm text-red-800">
                        <li>• Export type: Filtered (Download)</li>
                        <li>• Total Matching: {pagination?.totalItems || "-"}</li>
                        <li>• Format: {exportFormat.toUpperCase()}</li>
                        <li>• Uses your active filters</li>
                      </ul>
                    ) : (
                      <div className="mt-2 text-sm text-gray-600">
                        Filtered export is disabled. Apply a filter (date range,
                        search, or network) to enable download export.
                      </div>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={() => setShowExportModal(false)}
                      className="flex-1 px-4 py-2 font-medium text-gray-700 rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleFilteredExportDownload}
                      disabled={!hasActiveFilters || exportingFiltered}
                      className={`flex flex-1 gap-2 justify-center items-center px-4 py-2 font-medium rounded-lg transition-all ${
                        !hasActiveFilters || exportingFiltered
                          ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                          : "text-white bg-gradient-to-r from-[#FF6C6C] to-[#FF5252] hover:shadow-lg"
                      }`}
                    >
                      <Download className="w-4 h-4" />
                      {exportingFiltered ? "Exporting..." : "Export Filtered"}
                    </button>
                  </div>

                  <div className="pt-4 mt-4 border-t border-gray-200">
                    <button
                      onClick={handleExportAllToEmail}
                      disabled={exportingAll}
                      className={`w-full flex gap-2 justify-center items-center px-4 py-2 font-medium text-white rounded-lg transition-all ${
                        exportingAll
                          ? "bg-gray-400 cursor-not-allowed"
                          : "bg-gradient-to-r from-gray-800 to-gray-700 hover:shadow-lg"
                      }`}
                    >
                      <Mail className="w-4 h-4" />
                      {exportingAll
                        ? "Sending export to email..."
                        : "Export ALL Data to Email"}
                    </button>
                    <p className="mt-2 text-xs text-center text-gray-600">
                      This will send the full export to the logged-in admin’s
                      email.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          )}
        </AnimatePresence>
      </div>
    </>
  );
};

export default PotentialPublishers;
